import React, { useEffect, useMemo, useState } from 'react'
import CollapsibleCheckbox from './CollapsibleCheckbox'
import CollapsibleRadio from '../../DownloadData/CollapsibleRadio'
import Wrapper from './VisualizeByNameFilter.styles'
import { DownArrow } from "../../../assets/Shared/Icons/DownArrow/DownArrow";
import { UpArrow } from "../../../assets/Shared/Icons/UpArrow/UpArrow";
import { Badge, BADGE_TYPE_NEUTRAL_DEFAULT, Checkbox, Radio, Select, SIZE_MEDIUM } from '@mds/mds-reactjs-library';
import { useNavigate } from "react-router-dom";
import { NavigationRoutes } from '../../../Constants/Routes'
import ArrowRightIcon from '../../../assets/arrow-right-icon.svg'
import { numberToWords } from '../../../Utility/SharedUtility'
import PortalSearchBar from '../../Shared/PortalSearchBar'
import LiveDashboardAssetClassFilter from './LiveDashboardAssetClassFilter'
import LiveDashboardGeographyFilter from './LiveDashboardGeographyFilter'
import LiveDashboardCurrencyFilter from './LiveDashboardCurrencyFilter'
import {
    getAssetClassSelectionDisplayCount,
    isLiveDashboardAssetClassDimension,
} from './liveDashboardAssetClass'
import { isLiveDashboardGeographyDimension } from './liveDashboardGeography'
import { isLiveDashboardCurrencyDimension } from './liveDashboardCurrency'

const ALL_VEHICLES_TOTAL_DISPLAY = 'All Vehicles (Total)'

const isPlaceholder = (item) => {
    if (!item?.Details) return false
    try {
        const details = typeof item.Details === 'string' ? JSON.parse(item.Details) : item.Details
        return details.placeholder === true
    } catch {
        return false
    }
}

const parseYearFromDisplay = (displayName) => {
    const m = String(displayName ?? '').match(/\d{4}/)
    return m ? parseInt(m[0], 10) : NaN
}

const parseYearGroup = (displayName) => {
    const str = String(displayName ?? '').trim()
    const colonIdx = str.indexOf(':')
    if (colonIdx < 0) return ''
    return str.substring(0, colonIdx).trim().toLowerCase()
}

const SIMPLE_GROUP = ''

const collectYearLeaves = (attributes) => {
    const out = []
    const walk = (nodes) => {
        for (const n of nodes || []) {
            if (n.Children && n.Children.length > 0) walk(n.Children)
            else out.push(n)
        }
    }
    walk(attributes)
    return out
}

const sortAttrsByYear = (attrs) =>
    [...attrs].sort((a, b) => {
        const ya = parseYearFromDisplay(a.DisplayName)
        const yb = parseYearFromDisplay(b.DisplayName)
        if (!Number.isNaN(ya) && !Number.isNaN(yb)) return ya - yb
        return String(a.DisplayName).localeCompare(String(b.DisplayName))
    })

const yearSelectPortalProps =
    typeof document !== 'undefined'
        ? { menuPortalTarget: document.body, menuPosition: 'fixed', maxMenuHeight: 280 }
        : {}

const VisualizeByNameFilter = ({ dimension, openAccordionDimensionId, setOpenAccordionDimensionId, selection, setSelection, showDefinition, chart, chartConfiguration, setValidationMessage, headMessage }) => {

    const [searchTerm, setSearchTerm] = useState('')
    const [, setSelectionIsValid] = useState(true)
    const [showSelected, setShowSelected] = useState(false)
    const [dimensionConfiguration, setDimensionConfiguration] = useState({});
    const [singleSelect, setSingleSelect] = useState(JSON.parse(dimension.Details).singleSelect);
    const [yearAnchor, setYearAnchor] = useState(null)

    const isYearsDimension = dimension.DisplayName === 'Years'
    const isLiveDashboardGeography = isLiveDashboardGeographyDimension(chart, dimension)
    const isLiveDashboardAssetClass = isLiveDashboardAssetClassDimension(chart, dimension)
    const isLiveDashboardCurrency = isLiveDashboardCurrencyDimension(chart, dimension)
    const selectionBadgeCount = useMemo(() => {
        const attributeIds = selection?.Attributes
        if (!Array.isArray(attributeIds)) {
            return 0
        }
        if (isLiveDashboardAssetClass) {
            return getAssetClassSelectionDisplayCount(dimension, attributeIds)
        }
        return attributeIds.length
    }, [dimension, isLiveDashboardAssetClass, selection?.Attributes])
    const yearLeaves = useMemo(() => collectYearLeaves(dimension.Attributes || []), [dimension.Attributes])

    const { startYearId, endYearId } = useMemo(() => {
        if (!isYearsDimension) return { startYearId: null, endYearId: null }
        const ids = selection?.Attributes || []
        const attrs = ids
            .map((id) => yearLeaves.find((a) => a.AttributeID === id || String(a.AttributeID) === String(id)))
            .filter(Boolean)
        const sorted = sortAttrsByYear(attrs)
        if (sorted.length >= 2) {
            return { startYearId: sorted[0].AttributeID, endYearId: sorted[sorted.length - 1].AttributeID }
        }
        if (sorted.length === 1) {
            if (yearAnchor === 'end') return { startYearId: null, endYearId: sorted[0].AttributeID }
            return { startYearId: sorted[0].AttributeID, endYearId: null }
        }
        return { startYearId: null, endYearId: null }
    }, [isYearsDimension, selection?.Attributes, yearLeaves, yearAnchor])

    useEffect(() => {
        if (!isYearsDimension) return
        if (!selection?.Attributes?.length) setYearAnchor(null)
    }, [isYearsDimension, selection?.Attributes?.length])

    const startYearAttr = useMemo(
        () => yearLeaves.find((x) => x.AttributeID === startYearId || String(x.AttributeID) === String(startYearId)) || null,
        [yearLeaves, startYearId]
    )
    const endYearAttr = useMemo(
        () => yearLeaves.find((x) => x.AttributeID === endYearId || String(x.AttributeID) === String(endYearId)) || null,
        [yearLeaves, endYearId]
    )

    const startYearNum = useMemo(() => startYearAttr ? parseYearFromDisplay(startYearAttr.DisplayName) : NaN, [startYearAttr])
    const endYearNum = useMemo(() => endYearAttr ? parseYearFromDisplay(endYearAttr.DisplayName) : NaN, [endYearAttr])
    const startYearGroup = useMemo(() => startYearAttr ? parseYearGroup(startYearAttr.DisplayName) : null, [startYearAttr])
    const endYearGroup = useMemo(() => endYearAttr ? parseYearGroup(endYearAttr.DisplayName) : null, [endYearAttr])

    const startYearOptions = useMemo(
        () =>
            yearLeaves.map((attr) => {
                const group = parseYearGroup(attr.DisplayName)
                const y = parseYearFromDisplay(attr.DisplayName)
                let disabled = false
                if (endYearGroup !== null) {
                    const compatible = group === endYearGroup || group === SIMPLE_GROUP || endYearGroup === SIMPLE_GROUP
                    if (!compatible) disabled = true
                }
                if (!disabled && !Number.isNaN(endYearNum) && !Number.isNaN(y) && y > endYearNum) {
                    disabled = true
                }
                return {
                    label: attr.DisplayName,
                    value: String(attr.AttributeID),
                    disabled,
                }
            }),
        [yearLeaves, endYearGroup, endYearNum]
    )

    const endYearOptions = useMemo(
        () =>
            yearLeaves.map((attr) => {
                const group = parseYearGroup(attr.DisplayName)
                const y = parseYearFromDisplay(attr.DisplayName)
                let disabled = false
                if (startYearGroup !== null) {
                    const compatible = group === startYearGroup || group === SIMPLE_GROUP || startYearGroup === SIMPLE_GROUP
                    if (!compatible) disabled = true
                }
                if (!disabled && !Number.isNaN(startYearNum) && !Number.isNaN(y) && y < startYearNum) {
                    disabled = true
                }
                return {
                    label: attr.DisplayName,
                    value: String(attr.AttributeID),
                    disabled,
                }
            }),
        [yearLeaves, startYearGroup, startYearNum]
    )

    const setYearPairSelection = (startAttr, endAttr) => {
        if (!startAttr && !endAttr) {
            setSelection(dimension.DimensionID, [])
            return
        }
        if (startAttr && !endAttr) {
            setSelection(dimension.DimensionID, [startAttr.AttributeID])
            return
        }
        if (!startAttr && endAttr) {
            setSelection(dimension.DimensionID, [endAttr.AttributeID])
            return
        }
        const pair = sortAttrsByYear([startAttr, endAttr])
        setSelection(dimension.DimensionID, pair.map((a) => a.AttributeID))
    }

    const onStartYearSelectChange = (v) => {
        setYearAnchor('start')
        setValidationMessage('')
        const val = v === undefined || v === null ? '' : String(v)
        const endAttr = yearLeaves.find((x) => x.AttributeID === endYearId || String(x.AttributeID) === String(endYearId))
        if (!val) {
            if (endAttr) setYearAnchor('end')
            setYearPairSelection(null, endAttr || null)
            return
        }
        const startAttr = yearLeaves.find((x) => String(x.AttributeID) === val)
        if (!startAttr) return
        const sy = parseYearFromDisplay(startAttr.DisplayName)
        const sg = parseYearGroup(startAttr.DisplayName)
        if (endAttr) {
            const ey = parseYearFromDisplay(endAttr.DisplayName)
            const eg = parseYearGroup(endAttr.DisplayName)
            const groupsCompatible = sg === eg || sg === SIMPLE_GROUP || eg === SIMPLE_GROUP
            if (!groupsCompatible || (!Number.isNaN(sy) && !Number.isNaN(ey) && sy > ey)) {
                setYearPairSelection(startAttr, null)
                return
            }
        }
        setYearPairSelection(startAttr, endAttr || null)
    }

    const onEndYearSelectChange = (v) => {
        setYearAnchor('end')
        setValidationMessage('')
        const val = v === undefined || v === null ? '' : String(v)
        const startAttr = yearLeaves.find((x) => x.AttributeID === startYearId || String(x.AttributeID) === String(startYearId))
        if (!val) {
            if (startAttr) setYearAnchor('start')
            setYearPairSelection(startAttr || null, null)
            return
        }
        const endAttr = yearLeaves.find((x) => String(x.AttributeID) === val)
        if (!endAttr) return
        const ey = parseYearFromDisplay(endAttr.DisplayName)
        const eg = parseYearGroup(endAttr.DisplayName)
        if (startAttr) {
            const sy = parseYearFromDisplay(startAttr.DisplayName)
            const sg = parseYearGroup(startAttr.DisplayName)
            const groupsCompatible = sg === eg || sg === SIMPLE_GROUP || eg === SIMPLE_GROUP
            if (!groupsCompatible || (!Number.isNaN(sy) && !Number.isNaN(ey) && ey < sy)) {
                setYearPairSelection(null, endAttr)
                return
            }
        }
        setYearPairSelection(startAttr || null, endAttr)
    }

    const navigate = useNavigate();

    useEffect(() => {
        const childrenElements = document.querySelectorAll('.children');
        if ((searchTerm !== undefined && searchTerm.trim() !== "") || showSelected) {
            childrenElements.forEach((element) => {
                element.classList.replace('hideChildren', 'showChildren');
            });
        }
        else {
            childrenElements.forEach((element) => {
                element.classList.replace('showChildren', 'hideChildren');
            });
        }
    }, [searchTerm, showSelected]);

    useEffect(() => {
        const dimensionConfiguration = chartConfiguration?.Dimensions?.[dimension.Name] || {};
        setDimensionConfiguration(dimensionConfiguration);
        setSingleSelect(JSON.parse(dimension.Details).singleSelect || dimensionConfiguration.SingleSelect);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart])

    const handleNavigation = () => {
        navigate(NavigationRoutes.SUPPORT.Product_Hierarchy);
    };

    const checkBoxClicked = (item) => {
        if (singleSelect) {
            if (isPlaceholder(item)) return
            if (selection?.Attributes?.includes(item.AttributeID)) return
            setSelection(dimension.DimensionID, [item.AttributeID])
            return
        }
        setSelectionIsValid(true);
        setValidationMessage('');

        if (!isNaN(dimensionConfiguration.MinSelections) && dimensionConfiguration.MinSelections > selection?.Attributes?.length + 1) {
            setSelectionIsValid(false);
            setValidationMessage(`Please select at least ${numberToWords(dimensionConfiguration.MinSelections)} ${dimensionConfiguration.MinSelections === 1 ? 'item' : 'items'} for ${dimension.DisplayName}`);
        }

        if (!isNaN(dimensionConfiguration.MaxSelections) && dimensionConfiguration.MaxSelections < selection?.Attributes?.length + 1) {
            if (!selection?.Attributes?.includes(item?.AttributeID)) {
                setSelectionIsValid(false);
                setValidationMessage(`Filter limit exceeded. Select up to ${numberToWords(dimensionConfiguration.MaxSelections)} ${dimensionConfiguration.MaxSelections === 1 ? 'item' : 'items'} for ${dimension.DisplayName}`);
                return;
            }
        }

        if ((!item.Children || item.Children.length === 0) && !selection?.Attributes?.includes(item.AttributeID)) {
            setSelection(item.DimensionID, [...selection.Attributes, item.AttributeID]);
            return;
        }
        else if (item.Children && item.Children.length === 0 && selection.Attributes.includes(item.AttributeID)) {
            deSelectItem(item);
        }

        selectItem(item);
    }

    const selectItem = (item) => {
        if (singleSelect) {
            setSelection(dimension.DimensionID, [item.AttributeID])
        }
        else {

            if (item.Children && item.Children.length > 0 && !selection?.Attributes?.includes(item.AttributeID)) {
                setSelection(item.DimensionID, [...selection.Attributes, item.AttributeID]);
                return;
            }

            const collectAttributeIDs = (item) => {
                let attributeIDs = [item.AttributeID];

                if (Array.isArray(item.Children)) {
                    item.Children.forEach(child => {
                        attributeIDs = [...attributeIDs, ...collectAttributeIDs(child)];
                    });
                }

                return attributeIDs;
            };

            let allAttributeIDs = [
                ...collectAttributeIDs(item)
            ];

            allAttributeIDs = allAttributeIDs.filter(value => value !== item.AttributeID);
            const allChildsCount = allAttributeIDs.length;
            const allSelectedChildsCount = selection.Attributes.filter(val => allAttributeIDs.includes(val)).length;
            if (selection.Attributes.includes(item.AttributeID) && ((allChildsCount) !== allSelectedChildsCount)) {
                const selectedCount = selection.Attributes.filter(val => !allAttributeIDs.includes(val)).length;

                if (!isNaN(dimensionConfiguration.MaxSelections) && dimensionConfiguration.MaxSelections < (allChildsCount + selectedCount)) {
                    deSelectItem(item);
                    setSelectionIsValid(false);
                    setValidationMessage(`Filter limit exceeded. Select up to ${numberToWords(dimensionConfiguration.MaxSelections)} ${dimensionConfiguration.MaxSelections === 1 ? 'item' : 'items'} for ${dimension.DisplayName}`);
                    return;
                }
                const uniqueAttributes = [...new Set([...selection.Attributes, ...allAttributeIDs])];
                setSelection(item.DimensionID, uniqueAttributes);
                return
            }
            else if (selection.Attributes.includes(item.AttributeID)) {
                deSelectItem(item, allAttributeIDs);
                return
            }
        }
    }

    const deSelectItem = (item, idsToRemove) => {
        if (singleSelect) {
            return;
        }
        else {
            if (!isNaN(dimensionConfiguration.MinSelections) && dimensionConfiguration.MinSelections > selection?.Attributes?.length - 1) {
                setSelectionIsValid(false);
                setValidationMessage(`Please select at least ${numberToWords(dimensionConfiguration.MinSelections)} ${dimensionConfiguration.MinSelections === 1 ? 'item' : 'items'} for ${dimension.DisplayName}`);
            }
            if (idsToRemove !== undefined)
                selection.Attributes = selection.Attributes.filter(id => !idsToRemove.includes(id));
            selection.Attributes.splice(selection.Attributes.indexOf(item.AttributeID), 1);
            setSelection(item.DimensionID, selection.Attributes);
        }
    }

    const showItem = (item) => {
        if ((item.DisplayName.toLowerCase().includes(searchTerm.toLowerCase()) || (showDefinition !== undefined && showDefinition && item.Comment.toLowerCase().includes(searchTerm.toLowerCase()))) && (!showSelected || selection.Attributes.includes(item.AttributeID))) {
            return true;
        }
        return false;
    }

    const clearSelection = (e, dimensionId) => {
        setSelection(dimensionId, []);
        e.stopPropagation();
    }

    const isSelected = (item) => {
        const findValue = selection?.Attributes?.includes(item.AttributeID);
        return !!findValue || findValue === 0;
    }

    return (
        <Wrapper style={{ flex: openAccordionDimensionId === dimension.DimensionID ? 1 : 'none' }} >
            <div className="item" tabIndex="0"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setOpenAccordionDimensionId(openAccordionDimensionId === dimension.DimensionID ? -1 : dimension.DimensionID);
                    }
                }} onClick={() => { setOpenAccordionDimensionId(openAccordionDimensionId === dimension.DimensionID ? -1 : dimension.DimensionID); }}>
                <div>
                    <div className="dimension-name-container">
                        <p>{dimension.DisplayName}</p>
                        {
                            !singleSelect && <Badge type={BADGE_TYPE_NEUTRAL_DEFAULT} numeric>{selectionBadgeCount}</Badge>
                        }
                    </div>
                    {dimension.DisplayName === "Products" && (
                        <button
                            onClick={handleNavigation}
                            className="go-to-hierarchy-button"
                        >
                            View product hierarchy <img className="arrow-icon" src={ArrowRightIcon} alt="view" />
                        </button>
                    )}
                </div>
                <div className="spacer"></div>
                <div className="clearAlign">
                    {
                        !singleSelect && <input type="button" className='clear' value="Clear" onClick={e => { clearSelection(e, dimension.DimensionID); if (isYearsDimension) setYearAnchor(null); }} />
                    }
                    <div>
                        {openAccordionDimensionId === dimension.DimensionID ? <UpArrow color='#333333' size={14} /> : <DownArrow color='#333333' size={14} />}
                    </div>
                </div>
            </div>
            {
                openAccordionDimensionId === dimension.DimensionID &&
                <div className="popup-items">
                    {/* {!isYearsDimension && (
                    <div className="controls">
                        <PortalSearchBar
                            className="portal-search-vbn"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                            placeholder="Search"
                            marginBottom="0"
                            maxWidth="none"
                            autoFocus
                        />
                        {
                            !singleSelect &&
                            <div className="show-selected" tabIndex="1"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setShowSelected(value => !value);
                                        setSearchTerm('');
                                    }
                                }} onClick={() => { setShowSelected(value => !value); setSearchTerm('') }}>
                                {showSelected ? "Show all" : "Show selected"}
                            </div>
                        }
                    </div>
                    )} */}
                    {/* {
                        <div className="status"> {validationMessage} </div>
                    } */}
                    {isLiveDashboardGeography ? (
                    <div className="flex1 flex1-years">
                        <LiveDashboardGeographyFilter
                            dimension={dimension}
                            selection={selection}
                            setSelection={setSelection}
                            setValidationMessage={setValidationMessage}
                            dimensionConfiguration={dimensionConfiguration}
                        />
                    </div>
                    ) : isLiveDashboardAssetClass ? (
                    <div className="flex1 flex1-years">
                        <LiveDashboardAssetClassFilter
                            dimension={dimension}
                            selection={selection}
                            setSelection={setSelection}
                            setValidationMessage={setValidationMessage}
                            dimensionConfiguration={dimensionConfiguration}
                        />
                    </div>
                    ) : isLiveDashboardCurrency ? (
                    <div className="flex1 flex1-years">
                        <LiveDashboardCurrencyFilter
                            dimension={dimension}
                            selection={selection}
                            setSelection={setSelection}
                            setValidationMessage={setValidationMessage}
                            dimensionConfiguration={dimensionConfiguration}
                        />
                    </div>
                    ) : isYearsDimension ? (
                    <div className="flex1 flex1-years">
                        <div className="years-range-row">
                            <div className="years-range-field">
                                <label htmlFor={`start-year-${dimension.DimensionID}`}>Start Year</label>
                                <Select
                                    inputId={`start-year-${dimension.DimensionID}`}
                                    custom
                                    placeholder="Select start year"
                                    size={SIZE_MEDIUM}
                                    width="100%"
                                    value={startYearId != null && startYearId !== '' ? String(startYearId) : ''}
                                    options={startYearOptions}
                                    onChange={onStartYearSelectChange}
                                    {...yearSelectPortalProps}
                                />
                            </div>
                            <div className="years-range-field">
                                <label htmlFor={`end-year-${dimension.DimensionID}`}>End Year</label>
                                <Select
                                    inputId={`end-year-${dimension.DimensionID}`}
                                    custom
                                    placeholder="Select end year"
                                    size={SIZE_MEDIUM}
                                    width="100%"
                                    value={endYearId != null && endYearId !== '' ? String(endYearId) : ''}
                                    options={endYearOptions}
                                    onChange={onEndYearSelectChange}
                                    {...yearSelectPortalProps}
                                />
                            </div>
                        </div>
                    </div>
                    ) : (
                    <div className="flex1">
                        {
                            dimension.Attributes
                                .map(item =>
                                    <div key={item.AttributeID} className='popup-item'>
                                        {
                                            singleSelect ? (
                                                <>
                                                    {item.Children && item.Children.length ? (
                                                        <CollapsibleRadio
                                                            level={0}
                                                            className={item.ParentAttributeID ? "child" : ""}
                                                            checked={isSelected(item)}
                                                            item={item}
                                                            showItem={showItem}
                                                            checkBoxClicked={checkBoxClicked}
                                                            showDefinition={showDefinition}
                                                            isSelected={isSelected}
                                                            searchTerm={searchTerm}
                                                            isPlaceholder={isPlaceholder}
                                                        />
                                                    ) : showItem(item) && (
                                                        <div
                                                            className={
                                                                String(item.DisplayName ?? '').trim() === ALL_VEHICLES_TOTAL_DISPLAY
                                                                    ? 'flex vbn-radio-leaf-row--vehicles-total'
                                                                    : 'flex'
                                                            }
                                                        >
                                                            <div className="spacer"></div>
                                                            <div className="info" tabIndex={isPlaceholder(item) ? -1 : 0}
                                                                onKeyDown={isPlaceholder(item) ? undefined : (e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') checkBoxClicked(item)
                                                                }}
                                                                onClick={() => { !isPlaceholder(item) && checkBoxClicked(item) }}>
                                                                <p className="attribute-name">{item.DisplayName}</p>
                                                                {showDefinition && item.Comment && item.Comment !== 'NA' && (
                                                                    <p className="comment">{item.Comment}</p>
                                                                )}
                                                            </div>
                                                            {!isPlaceholder(item) && (
                                                                <div className="tree-selector-cell">
                                                                    <Radio
                                                                        value=""
                                                                        checked={isSelected(item)}
                                                                        label=""
                                                                        onClick={() => { checkBoxClicked(item) }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            ) :
                                                <>
                                                    {

                                                        item.Children && item.Children.length ?
                                                            <>

                                                                {

                                                                    <CollapsibleCheckbox
                                                                        level={0}
                                                                        className={item.ParentAttributeID ? "child" : ""}
                                                                        checked={isSelected(item)}
                                                                        label={item.DisplayName}
                                                                        item={item}
                                                                        showItem={showItem}
                                                                        checkBoxClicked={checkBoxClicked}
                                                                        showDefinition={showDefinition}
                                                                        selection={selection}
                                                                        isSelected={isSelected}
                                                                        dimensionConfiguration={dimensionConfiguration}
                                                                        searchTerm={searchTerm}
                                                                    />


                                                                }
                                                            </> :
                                                            (
                                                                showItem(item) &&
                                                                <Checkbox
                                                                    className={item.ParentAttributeID ? "child" : ""}
                                                                    checked={isSelected(item)}
                                                                    value=""
                                                                    label={item.DisplayName}
                                                                    item={item}
                                                                    onClick={() => { checkBoxClicked(item); }}
                                                                />
                                                            )
                                                    }
                                                </>
                                        }
                                    </div>
                                )
                        }
                    </div>
                    )}
                </div>
            }
        </Wrapper>
    )
}

export default VisualizeByNameFilter